import { calendarConfig } from './calendar.config';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { CalendarOptions, EventApi } from '@fullcalendar/core';
import { LanguageService } from '@core/services/language.service';
import { CalendarService } from '@modules/calendar/shared/services/calendar.service';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarEventPopoverComponent } from './components/calendar-event-popover/calendar-event-popover.component';
import { CalendarEvent } from '@shared/modules/event-sidenav/models/calendar-event.model';
import { EventSidenavController } from '@shared/modules/event-sidenav/controllers/event-sidenav.controller';
import { IFullCalendarView } from '@modules/calendar/shared/interfaces/fullcalendar-view.interface';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { Task } from '@shared/models/task.model';
import { Config } from '@shared/configs/config';
import * as moment from 'moment';
import { TaskController } from '@shared/controllers/task.controller';
import { ECalendarAction } from '@modules/calendar/shared/enums/calendar-action.enum';
import { EventController } from '@shared/controllers/event.controller';
import { catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'calendar-main-section',
  templateUrl: './calendar-main-section.component.html',
  styleUrls: ['./calendar-main-section.component.scss'],
  animations: [
    trigger('slide', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class CalendarMainSectionComponent implements OnInit, AfterViewInit {
  calendarOptions: CalendarOptions = calendarConfig(
    this.languageService,
    this.onEventSingleClick.bind(this),
    this.onEventDoubleClick.bind(this),
    this.onEventChange.bind(this)
  );
  openedPopover: { event: CalendarEvent; parentElement: HTMLBaseElement };

  @ViewChild('popoverComponent') popoverComponent: CalendarEventPopoverComponent;
  @ViewChild('fullCalendarComponent') fullCalendarComponent: FullCalendarComponent;

  constructor(
    public service: CalendarService,
    public languageService: LanguageService
  ) {}

  ngOnInit(): void {
    console.log('calendar main section');
  }

  ngAfterViewInit() {
    this.service.mainCtrl.registerFullCalendar(this.fullCalendarComponent);
    this.service.mainCtrl.getCalendarEvents();
  }

  private onEventChange(e: { event: EventApi; oldEvent: EventApi; relatedEvents?; revert?: Function }) {
    if (e.event?.extendedProps?.calendarEvent) {
      this.updateEvent(e);
      return;
    }

    if (e.event.extendedProps?.task) {
      this.updateTask(e);
      return;
    }
  }

  private updateEvent(e: { event: EventApi; oldEvent: EventApi; relatedEvents?; revert?: Function }) {
    const event = new CalendarEvent(e.event?.extendedProps?.calendarEvent);
    event.termEnd = this.getChangeEventNewDate(!e.event.end ? 'start' : 'end', e);
    event.termStart = this.getChangeEventNewDate('start', e);

    const ctrl = new EventController();
    const request = event.isGoogleEvent ? ctrl.updateGoogleEvent(event) : ctrl.updateEvent(event);

    request
      ?.pipe(
        catchError(() => {
          e.revert?.();
          return EMPTY;
        })
      )
      .subscribe(() => this.service.subCalendarCtrl.calendarEmmiter.emit({ action: ECalendarAction.UPDATE_VIEW }));
  }

  private updateTask(e: { event: EventApi; oldEvent: EventApi; relatedEvents?; revert?: Function }) {
    const task = new Task(e.event?.extendedProps?.task);
    task.termStart = this.getChangeEventNewDate('start', e);
    task.termEnd = this.getChangeEventNewDate('end', e);

    const ctrl = new TaskController(task);
    ctrl
      .updateTask(task)
      ?.subscribe(() =>
        this.service.subCalendarCtrl.calendarEmmiter.emit({ action: ECalendarAction.UPDATE_VIEW })
      );
  }

  private getChangeEventNewDate(type: string, e: { event: EventApi; oldEvent: EventApi; relatedEvents? }) {
    if (!!e?.event?.allDay && type === 'end') {
      return e.event[type]
        ? moment(e.event[type]).subtract(1, 'day').endOf('day').format(Config.DATE_SERVER)
        : moment(e.oldEvent[type]).subtract(1, 'day').endOf('day').format(Config.DATE_SERVER);
    }
    return e.event[type]
      ? moment(e.event[type]).format(Config.DATE_SERVER)
      : moment(e.oldEvent[type]).format(Config.DATE_SERVER);
  }

  canPreviewEvent() {
    return true;
  }

  canPreviewTask(task: Task) {
    return true;
  }

  onEventDoubleClick(e: IFullCalendarView) {
    if (e.event.extendedProps?.calendarEvent) {
      const event = e.event.extendedProps?.calendarEvent || null;
      if (e.event.extendedProps?.isExternal) {
        event ? this.openEventPreview(e) : null;
        return;
      }

      if (!this.canPreviewEvent()) return;
      event ? this.openEventPreview(e) : null;
      return;
    }

    const task = e.event.extendedProps?.task || null;
    if (!this.canPreviewTask(task)) return;
    task ? this.openTaskPreview(e, task) : null;
  }

  onEventSingleClick(e: IFullCalendarView) {
    if (e.event.extendedProps?.calendarEvent) {
      if (!this.canPreviewEvent()) return;
      const event = this.getEvent(e);
      if (event?.id || event?.googleEventId) {
        this.openEventPreview(e);
        return;
      }
      this.openPopover(event, e.el as HTMLBaseElement);
      return;
    }

    const task = e.event.extendedProps?.task || null;
    if (!this.canPreviewTask(task)) return;
    task ? this.openTaskPreview(e, task) : null;
  }

  openEventPreview(e: IFullCalendarView) {
    const ctrl = new EventSidenavController();
    ctrl.openPreview(e.event.extendedProps?.calendarEvent);
  }

  openTaskPreview(e: IFullCalendarView, task) {
    const event = this.getTask(e);
    const ctrl = new TaskSidenavController();
    ctrl.previewTask(String(event.id));
  }

  getEvent(e): CalendarEvent {
    const def = e.event;
    return Object.assign(def.extendedProps.calendarEvent, {
      color: def?.ui?.backgroundColor
    }) as CalendarEvent;
  }

  getTask(e): Task {
    const def = e.event;
    return new Task(def?.extendedProps?.task);
  }

  private openPopover(event: CalendarEvent, el: HTMLBaseElement) {
    this.openedPopover = {
      event: event,
      parentElement: el
    };
  }
}
